<template>
  <div class="error-test-container">
    <h3>Error Handling System Test</h3>
    <p>Click the buttons below to test different error scenarios:</p>

    <el-row :gutter="20">
      <el-col :span="8">
        <el-card>
          <div slot="header">
            <span>Network Errors</span>
          </div>
          <el-button @click="testNetworkError" type="warning" block>
            Test Network Error
          </el-button>
          <el-button @click="testDatabaseError" type="warning" block style="margin-top: 10px;">
            Test Database Error
          </el-button>
        </el-card>
      </el-col>

      <el-col :span="8">
        <el-card>
          <div slot="header">
            <span>HTTP Errors</span>
          </div>
          <el-button @click="testValidationError" type="danger" block>
            Test 422 Validation Error
          </el-button>
          <el-button @click="testForbiddenError" type="danger" block style="margin-top: 10px;">
            Test 403 Forbidden
          </el-button>
          <el-button @click="testServerError" type="danger" block style="margin-top: 10px;">
            Test 500 Server Error
          </el-button>
        </el-card>
      </el-col>

      <el-col :span="8">
        <el-card>
          <div slot="header">
            <span>Success Messages</span>
          </div>
          <el-button @click="testSuccess" type="success" block>
            Test Success Message
          </el-button>
          <el-button @click="testWarning" type="warning" block style="margin-top: 10px;">
            Test Warning Message
          </el-button>
          <el-button @click="testInfo" type="info" block style="margin-top: 10px;">
            Test Info Message
          </el-button>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" style="margin-top: 20px;">
      <el-col :span="12">
        <el-card>
          <div slot="header">
            <span>API Call with Loading</span>
          </div>
          <el-button @click="testApiCallWithLoading" type="primary" block :loading="loading">
            Test API Call with Loading
          </el-button>
          <p v-if="apiResult" style="margin-top: 10px; color: green;">
            API Result: {{ apiResult }}
          </p>
        </el-card>
      </el-col>

      <el-col :span="12">
        <el-card>
          <div slot="header">
            <span>Form Validation</span>
          </div>
          <el-form ref="testForm" :model="form" :rules="rules">
            <el-form-item label="Name" prop="name">
              <el-input v-model="form.name" placeholder="Enter name"></el-input>
            </el-form-item>
            <el-form-item label="Email" prop="email">
              <el-input v-model="form.email" placeholder="Enter email"></el-input>
            </el-form-item>
            <el-button @click="testFormValidation" type="primary">
              Test Form Validation
            </el-button>
          </el-form>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
export default {
  name: 'ErrorTest',
  data () {
    return {
      loading: false,
      apiResult: null,
      form: {
        name: '',
        email: ''
      },
      rules: {
        name: [
          { required: true, message: 'Name is required', trigger: 'blur' }
        ],
        email: [
          { required: true, message: 'Email is required', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email', trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    // Test network error
    testNetworkError () {
      const error = new Error('Network Error')
      error.message = 'Network Error'
      this.handleError(error, {
        component: 'ErrorTest',
        action: 'testNetworkError'
      })
    },

    // Test database error
    testDatabaseError () {
      const error = new Error('Database connection failed')
      error.response = {
        status: 500,
        data: {
          message: 'SQLSTATE[HY000] [2002] Connection refused'
        }
      }
      this.handleError(error, {
        component: 'ErrorTest',
        action: 'testDatabaseError'
      })
    },

    // Test validation error
    testValidationError () {
      const error = new Error('Validation failed')
      error.response = {
        status: 422,
        data: {
          name: ['The name field is required.'],
          email: ['The email field must be a valid email address.']
        }
      }
      this.handleError(error, {
        component: 'ErrorTest',
        action: 'testValidationError'
      })
    },

    // Test forbidden error
    testForbiddenError () {
      const error = new Error('Access denied')
      error.response = {
        status: 403,
        data: {
          message: 'You do not have permission to perform this action.'
        }
      }
      this.handleError(error, {
        component: 'ErrorTest',
        action: 'testForbiddenError'
      })
    },

    // Test server error
    testServerError () {
      const error = new Error('Server error')
      error.response = {
        status: 500,
        data: {
          message: 'An unexpected error occurred on the server.'
        }
      }
      this.handleError(error, {
        component: 'ErrorTest',
        action: 'testServerError'
      })
    },

    // Test success message
    testSuccess () {
      this.showSuccess('This is a test success message!')
    },

    // Test warning message
    testWarning () {
      this.showWarning('This is a test warning message!')
    },

    // Test info message
    testInfo () {
      this.showInfo('This is a test info message!')
    },

    // Test API call with loading
    async testApiCallWithLoading () {
      try {
        await this.withLoading(async () => {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 2000))
          this.apiResult = 'API call completed successfully!'
          this.showSuccess('API call completed!')
        }, 'loading', {
          component: 'ErrorTest',
          action: 'testApiCallWithLoading'
        })
      } catch (error) {
        // Error is handled by withLoading
      }
    },

    // Test form validation
    async testFormValidation () {
      try {
        await this.$refs.testForm.validate()
        this.showSuccess('Form validation passed!')
      } catch (error) {
        // Simulate validation error from server
        const validationError = new Error('Validation failed')
        validationError.response = {
          status: 422,
          data: {
            name: ['The name field is required.'],
            email: ['The email field must be a valid email address.']
          }
        }
        this.handleValidationError(validationError, 'testForm')
      }
    }
  }
}
</script>

<style scoped>
.error-test-container {
  padding: 20px;
}

.el-card {
  margin-bottom: 20px;
}

.el-button {
  margin-bottom: 10px;
}
</style>
